import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ThomasScottLogo from "../assets/logo.png";
import LoginLogo from "../assets/LoginIcon.png";
import Text from "@/components/common/Text";
import { Auth0LoginButton } from "@/auth/Auth0LoginButton";
import { peekReturnTo } from "@/auth/authRecoveryRef";

export default function SignInPage() {
  // SP4 launcher hotfix: when a user arrives here unauthenticated (most often
  // via the Threads launcher tile on the superapp dashboard), kick straight
  // into Auth0's universal login. If their Auth0 session is still live from
  // a recent superapp sign-in, Auth0 returns silently without a prompt.
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) return;
    // If the user just clicked "Sign out", do NOT auto-redirect into Auth0 — a
    // lingering or shared (superapp) SSO session would silently sign them right
    // back in. Show the interactive sign-in screen instead, and consume the
    // one-shot flag so the NEXT visit (e.g. arriving from the superapp launcher)
    // still gets the seamless silent SSO.
    if (localStorage.getItem("ts_explicit_logout")) {
      localStorage.removeItem("ts_explicit_logout");
      setShowFallback(true);
      return;
    }
    // When we got here via auth recovery, this redirect races the one recovery
    // itself fired — so read the same remembered destination rather than
    // hardcoding "/", or whichever redirect wins decides where the user lands.
    void loginWithRedirect({ appState: { returnTo: peekReturnTo() } });
    // The redirect is a full-page navigation that should whisk the user away
    // immediately. Only if it hasn't after a few seconds (e.g. it was blocked)
    // do we reveal the manual sign-in fallback — we never flash it during the
    // normal silent SSO flow.
    const timer = setTimeout(() => setShowFallback(true), 4000);
    return () => clearTimeout(timer);
  }, [isAuthenticated, isLoading, loginWithRedirect]);

  // Silent SSO: while Auth0 initializes and the redirect is in flight, show a
  // neutral loading state — never a login/signup screen — so arriving from the
  // superapp launcher is seamless.
  if (!showFallback) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-6 bg-white">
        <img src={ThomasScottLogo} alt="Thomas Scott" className="h-10 w-auto" />
        <Text variant="heading_h6" className="text-shades-neutral-300">
          Signing you in…
        </Text>
      </div>
    );
  }

  // Fallback (redirect blocked): the original interactive sign-in page.
  return (
    <div className="flex h-screen bg-white">
      <img
        src={LoginLogo}
        alt="Product logo"
        width={591}
        className="flex items-start justify-start"
      />
      <div className="flex flex-1 flex-col items-center justify-start gap-11 py-[98px]">
        <img src={ThomasScottLogo} alt="Product logo" className="mx-auto" />
        <Text
          variant="heading_h3"
          className="font-medium text-shades-neutral-300"
        >
          Apparel Dashboard
        </Text>
        <div className="flex items-center justify-center rounded-[24px] border border-shades-neutral-50 bg-[#FCFCFF] px-[58px] py-10">
          <Auth0LoginButton />
        </div>
      </div>
    </div>
  );
}
